import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import WarningDialogIcon from "@entrilia/shared/icons/WarningDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { workflowTypeCaptionMap } from "@entrilia/shared/utilities/enumCaptions";
import adminApi from "../../../../../api/adminApi";

interface Props {
  workflowId: string;
  workflowType: WorkflowType;
  onClose: () => void;
  onRetried: () => void;
}

const retryWorkflow = withErrorHandling(adminApi.retryWorkflow);

const RetryWorkflowDialog = ({ workflowId, workflowType, onClose, onRetried }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [retrying, setRetrying] = useState(false);

  const handleRetryWorkflow = async () => {
    setRetrying(true);
    const [, error] = await retryWorkflow(workflowId);
    setRetrying(false);

    if (error) {
      logError(error, "[RetryWorkflowDialog] retryWorkflow");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Workflow retry started");
    onRetried();
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Retry</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <WarningDialogIcon sx={{ width: 80, height: 80 }} />

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Retry {workflowTypeCaptionMap[workflowType] ?? workflowType}?
          </Typography>

          <Stack spacing={0.5}>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              The failed step will run again. Completed steps are kept.
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              If the failed step had already created records, for example a transaction in Fund Operations, check for
              them first to avoid duplicates.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelText="Close">
        <Button variant="contained" loading={retrying} onClick={handleRetryWorkflow}>
          Retry Workflow
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default RetryWorkflowDialog;
